import { type AppConfig, AppEnv } from '@brightdrop/app-config';
import type {
  FallbackLng,
  InitOptions,
  PostProcessorModule,
  TOptions,
} from 'i18next';
import type { DetectorOptions } from 'i18next-browser-languagedetector';
import { pseudoTransformString } from './pseudo-transform';

declare global {
  var appConfig: AppConfig;
}

/** Defining the default PSEUDO and SOURCE locales for the localization client. */
export const DEFAULT_PSEUDO_LOCALE = 'en-XA';
export const DEFAULT_SOURCE_LOCALE = 'en-US';

/** Set boolean flag to enable/disable pseudo localization. Only enabled in development env */
const isPseudoLocalizationEnabled = () =>
  globalThis.appConfig?.env === AppEnv.DEV;

type SupportedLngsOptions = {
  baseOptions: InitOptions;
  pseudoLocale?: string;
};

type FallbackLangsOptions = {
  baseOptions: InitOptions;
  pseudoLocale?: string;
  sourceLocale?: string;
};

type NavigatorDetectionOptions = {
  baseOptions: InitOptions;
};

const normalizeLocale = (code: string) => code.replace(/_/g, '-').toLowerCase();

type TranslatorLike = { language?: string };

export const buildPostProcessorModule = (
  pseudoLocale = DEFAULT_PSEUDO_LOCALE
): PostProcessorModule => {
  const targetLng = normalizeLocale(pseudoLocale);

  return {
    type: 'postProcessor',
    name: 'pseudo',
    process(
      value: string,
      _key: string,
      options: TOptions,
      translator?: TranslatorLike
    ): string {
      if (!isPseudoLocalizationEnabled() || value === '') return value;

      const lng = translator?.language ?? options?.lng;
      if (lng && normalizeLocale(String(lng)) !== targetLng) return value;

      return pseudoTransformString(value);
    },
  };
};

const buildSupportedLngs = ({
  baseOptions,
  pseudoLocale = DEFAULT_PSEUDO_LOCALE,
}: SupportedLngsOptions) => {
  if (!isPseudoLocalizationEnabled()) return;
  const baseSupportedLngs: string[] =
    (baseOptions.supportedLngs as string[]) || [];
  if (!baseSupportedLngs.includes(pseudoLocale))
    baseSupportedLngs.push(pseudoLocale);

  baseOptions.supportedLngs = baseSupportedLngs;
};

const buildFallbackLng = ({
  baseOptions,
  pseudoLocale = DEFAULT_PSEUDO_LOCALE,
  sourceLocale = DEFAULT_SOURCE_LOCALE,
}: FallbackLangsOptions) => {
  if (!isPseudoLocalizationEnabled()) return;
  let baseFallbackLng = baseOptions.fallbackLng;

  if (typeof baseOptions.fallbackLng === 'string') {
    baseFallbackLng = {
      default: [baseOptions.fallbackLng, sourceLocale],
      [pseudoLocale]: [sourceLocale],
    };
  }

  if (Array.isArray(baseOptions.fallbackLng)) {
    baseFallbackLng = {
      default: baseOptions.fallbackLng,
      [pseudoLocale]: [sourceLocale],
    };
  }

  if (typeof baseOptions.fallbackLng === 'object') {
    baseFallbackLng = {
      ...baseOptions.fallbackLng,
      [pseudoLocale]: [sourceLocale],
    };
  }

  baseOptions.fallbackLng = baseFallbackLng;
};

const buildNavigatorDetection = ({
  baseOptions,
}: NavigatorDetectionOptions) => {
  if (!isPseudoLocalizationEnabled()) return;

  if (typeof baseOptions.detection === 'object') {
    baseOptions.detection = {
      ...baseOptions.detection,
      order: ['cookie', 'navigator'],
      lookupCookie: 'locale',
    };
  }
};

/**
 * Builds an `InitOptions` partial containing `fallbackLng`, `supportedLngs`,
 * `detection`, and `postProcess`. Spread the result into your `i18next.init()`
 * call alongside any other options (`resources`, `defaultNS`, etc.).
 *
 * Pseudo-localization is gated internally by `isPseudoLocalizationEnabled()`
 * (true when `globalThis.appConfig?.env === AppEnv.DEV`). No `isEnabled`
 * parameter is needed.
 *
 * When pseudo is enabled this function:
 * - Appends `DEFAULT_PSEUDO_LOCALE` (`en-XA`) to `supportedLngs`.
 * - Extends `fallbackLng` with `{ [pseudoLocale]: [sourceLocale] }` (handles
 *   string, array, and object shapes).
 * - Overrides detection `order: ['cookie', 'navigator']` and sets
 *   `lookupCookie: 'locale'`.
 * - Appends `'pseudo'` to `postProcess`.
 *
 * The default detection reads `locale` and does not write it. An empty
 * `caches` list is required: omitting it makes the detector store the
 * language in localStorage. Only the app that applies the profile locale
 * should pass `caches: ['cookie']`.
 *
 * @param fallbackLng - i18next `fallbackLng` value. Defaults to `{ default: ['en-US'] }`.
 * @param supportedLngs - Allowed language codes. Defaults to `['en-US']`.
 * @param detectionObj - `i18next-browser-languagedetector` options.
 *   Defaults to cookie `locale`, then the browser language, with no cache.
 *
 * @example
 * i18next
 *   .use(buildPostProcessorModule())
 *   .use(LanguageDetector)
 *   .use(initReactI18next)
 *   .init({
 *     resources: TranslationBundle,
 *     defaultNS: 'common',
 *     ...buildPseudoI18nOptions(
 *       { en: ['en-US'], default: ['en-US'] },
 *       ['en-US', 'en-CA', 'fr-CA', 'es-MX'],
 *       {
 *         order: ['cookie', 'navigator'],
 *         lookupCookie: 'locale',
 *         caches: ['cookie'],
 *         cookieOptions: { secure: true, sameSite: 'strict' },
 *       }
 *     ),
 *   });
 */

export const buildPseudoI18nOptions = (
  fallbackLng?: FallbackLng,
  supportedLngs?: readonly string[],
  detectionObj?: DetectorOptions
) => {
  // Defined init options
  const initOptions: InitOptions = {
    fallbackLng: fallbackLng ?? { default: ['en-US'] },
    supportedLngs: supportedLngs ?? ['en-US'],
    // Read `locale`, then the browser language. Do not cache: every remote
    // shares this default, and a write would replace the shell's cookie.
    // An empty list is required; the detector otherwise caches in localStorage.
    detection: detectionObj ?? {
      order: ['cookie', 'navigator'],
      lookupCookie: 'locale',
      caches: [],
    },
  };

  // Supported languages
  buildSupportedLngs({
    baseOptions: initOptions,
    pseudoLocale: DEFAULT_PSEUDO_LOCALE,
  });

  // Fallback languages
  buildFallbackLng({
    baseOptions: initOptions,
    pseudoLocale: DEFAULT_PSEUDO_LOCALE,
    sourceLocale: DEFAULT_SOURCE_LOCALE,
  });

  // Detection obj
  buildNavigatorDetection({
    baseOptions: initOptions,
  });

  if (isPseudoLocalizationEnabled()) {
    initOptions.postProcess = [...(initOptions.postProcess || []), 'pseudo'];
  }

  return initOptions;
};
